import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { t } from "../../i18n";
import "./ApiDocsPage.css";

const API_URL = process.env.REACT_APP_API_URL || "https://b.supchats.com";

function RequestBlock({ method, path, description, apiKey, onExecute, loading, requestUrl, response, copied, onCopy }) {
  return (
    <div className="api-binom-request">
      <p className="api-binom-request-desc">{description}</p>
      <div className="api-binom-request-header">
        <span className={`api-binom-method api-binom-method-${method.toLowerCase()}`}>{method}</span>
        <span className="api-binom-path">{path}</span>
        <button
          type="button"
          className="api-binom-execute"
          onClick={onExecute}
          disabled={loading || !apiKey}
        >
          {loading ? t("Loading...") : t("Execute")}
        </button>
      </div>

      {requestUrl && (
        <div className="api-binom-field">
          <label>{t("Request URL")}</label>
          <div className="api-binom-code-row">
            <code>{requestUrl}</code>
            <button type="button" className="api-binom-copy-sm" onClick={() => onCopy(requestUrl, "url")}>
              {copied === "url" ? "✓" : "⎘"}
            </button>
          </div>
        </div>
      )}

      {response && (
        <div className="api-binom-field">
          <label>{t("Server response")}</label>
          <pre className={response.success ? "success" : "error"}>
            {JSON.stringify(response.success ? response.data : response.error, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function ApiDocsPage({ token }) {
  const [apiKey, setApiKey] = useState("");
  const [allowed, setAllowed] = useState(true);
  const [loading, setLoading] = useState(null);
  const [lastResult, setLastResult] = useState({ id: null, requestUrl: "", response: null });
  const [copied, setCopied] = useState(null);

  const [agentId, setAgentId] = useState("");
  const [chatId, setChatId] = useState("");
  const [messageText, setMessageText] = useState("Hello!");

  useEffect(() => {
    const init = async () => {
      try {
        const [limits, keyRes] = await Promise.all([
          axios.get(`${API_URL}/user/limits`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api-key`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }),
        ]);
        const role = (limits.data?.limits?.role_name || limits.data?.limits?.role || "").toString().toLowerCase();
        const hasKey = !!keyRes.data?.api_key;
        setAllowed(hasKey || (role && role !== "free"));
        setApiKey(keyRes.data?.api_key || "");
      } catch (e) {
        setAllowed(false);
      }
    };
    init();
  }, [token]);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const headers = { "X-API-Key": apiKey };
  const opts = { headers, withCredentials: false };

  const run = async (id, fn) => {
    setLoading(id);
    setLastResult({ id: null, requestUrl: "", response: null });
    try {
      const { url, res } = await fn();
      setLastResult({ id, requestUrl: url, response: { success: true, data: res.data } });
    } catch (e) {
      setLastResult({ id, requestUrl: "", response: { success: false, error: e?.response?.data || e.message } });
    } finally {
      setLoading(null);
    }
  };

  const showFor = (id) => lastResult.id === id ? lastResult : { requestUrl: "", response: null };

  const executeGetAgents = () =>
    run("agents", async () => {
      const url = `${API_URL}/api/v1/agents`;
      const res = await axios.get(url, opts);
      return { url: `GET ${url}`, res };
    });

  const executeGetChats = () =>
    run("chats", async () => {
      const url = `${API_URL}/api/v1/chats`;
      const res = await axios.get(url, opts);
      return { url: `GET ${url}`, res };
    });

  const executeGetChatsByAgent = () =>
    run("chats-agent", async () => {
      const id = agentId.trim() || "123";
      const url = `${API_URL}/api/v1/chats?agent_id=${encodeURIComponent(id)}`;
      const res = await axios.get(url, opts);
      return { url: `GET ${url}`, res };
    });

  const executeGetAgentChatsWithMessages = () =>
    run("agent-chats-msg", async () => {
      const id = agentId.trim() || "123";
      const url = `${API_URL}/api/v1/agents/${encodeURIComponent(id)}/chats?include_messages=true&limit=10`;
      const res = await axios.get(url, opts);
      return { url: `GET ${url}`, res };
    });

  const executeGetChatDetail = () =>
    run("chat-detail", async () => {
      const id = chatId.trim();
      if (!id) throw new Error(t("Enter chat ID"));
      const url = `${API_URL}/api/v1/chats/${encodeURIComponent(id)}`;
      const res = await axios.get(url, opts);
      return { url: `GET ${url}`, res };
    });

  const executeSendMessage = () =>
    run("send-msg", async () => {
      const id = chatId.trim();
      if (!id) throw new Error(t("Enter chat ID"));
      const msg = messageText.trim() || "Hello!";
      const url = `${API_URL}/api/v1/chats/${encodeURIComponent(id)}/messages`;
      const res = await axios.post(url, { message: msg }, opts);
      return { url: `POST ${url}\nBody: ${JSON.stringify({ message: msg })}`, res };
    });

  if (!allowed) {
    return (
      <div className="api-binom">
        <h1>{t("API")}</h1>
        <p className="api-binom-lead">
          {t("API is a simple way to get data from Supchats in JSON format using HTTP requests.")}
        </p>
        <section className="api-binom-section">
          <h2>{t("Getting an API Key")}</h2>
          <p>
            {t("Go to")} <Link to="/settings">Settings</Link> → {t("API")}. {t("Fill in your contact information in the Account section, then click \"Request API access\".")}
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="api-binom">
      <h1>{t("API")}</h1>
      <p className="api-binom-lead">
        {t("API is a simple way to get data from Supchats in JSON format using HTTP requests. Add the X-API-Key header to your requests.")}
      </p>

      <nav className="api-binom-nav">
        <a href="#getting-key">{t("Getting an API Key")}</a>
        <a href="#requests">{t("Requests")}</a>
        <a href="#endpoints">{t("Endpoints")}</a>
        <a href="#widget">{t("Widget")}</a>
      </nav>

      <section id="getting-key" className="api-binom-section">
        <h2>{t("Getting an API Key")}</h2>
        <p>{t("Your API key is available in")} <Link to="/settings">Settings</Link> → {t("API")}.</p>
        <div className="api-binom-key-row">
          <code>{apiKey || "..."}</code>
          <button type="button" className="api-binom-copy" onClick={() => copy(apiKey, "key")}>
            {copied === "key" ? "✓" : t("Copy")}
          </button>
        </div>
        <p className="api-binom-note">{t("Pass the key in the header")} <code>X-API-Key: YOUR_KEY</code></p>
      </section>

      <section id="requests" className="api-binom-section">
        <h2>{t("Requests")}</h2>
        <p>{t("Try any request below. Enter agent ID or chat ID where required, then click Execute.")}</p>

        <h3 className="api-binom-h3">{t("List of agents")}</h3>
        <RequestBlock
          method="GET"
          path="/api/v1/agents"
          description={t("Get all agents in your account. Use agent IDs in the requests below.")}
          apiKey={apiKey}
          onExecute={executeGetAgents}
          loading={loading === "agents"}
          requestUrl={showFor("agents").requestUrl}
          response={showFor("agents").response}
          copied={copied}
          onCopy={copy}
        />

        <h3 className="api-binom-h3">{t("List of chats")}</h3>
        <RequestBlock
          method="GET"
          path="/api/v1/chats"
          description={t("Get all your chats (list only, without messages).")}
          apiKey={apiKey}
          onExecute={executeGetChats}
          loading={loading === "chats"}
          requestUrl={showFor("chats").requestUrl}
          response={showFor("chats").response}
          copied={copied}
          onCopy={copy}
        />

        <h3 className="api-binom-h3">{t("Chats filtered by agent")}</h3>
        <div className="api-binom-params">
          <div className="api-binom-param">
            <label>agent_id</label>
            <input
              type="text"
              placeholder="123"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="api-binom-input"
            />
          </div>
        </div>
        <RequestBlock
          method="GET"
          path={`/api/v1/chats?agent_id=${agentId.trim() || "{agent_id}"}`}
          description={t("Get chats for a specific agent (filter by agent_id).")}
          apiKey={apiKey}
          onExecute={executeGetChatsByAgent}
          loading={loading === "chats-agent"}
          requestUrl={showFor("chats-agent").requestUrl}
          response={showFor("chats-agent").response}
          copied={copied}
          onCopy={copy}
        />

        <h3 className="api-binom-h3">{t("Agent chats with messages (for parsing)")}</h3>
        <div className="api-binom-params">
          <div className="api-binom-param">
            <label>agent_id</label>
            <input
              type="text"
              placeholder="123"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className="api-binom-input"
            />
          </div>
        </div>
        <RequestBlock
          method="GET"
          path={`/api/v1/agents/${agentId.trim() || "{agent_id}"}/chats?include_messages=true`}
          description={t("Get chats with full message content for an agent. Use this to display chats in your own service or CRM.")}
          apiKey={apiKey}
          onExecute={executeGetAgentChatsWithMessages}
          loading={loading === "agent-chats-msg"}
          requestUrl={showFor("agent-chats-msg").requestUrl}
          response={showFor("agent-chats-msg").response}
          copied={copied}
          onCopy={copy}
        />

        <h3 className="api-binom-h3">{t("Chat details")}</h3>
        <div className="api-binom-params">
          <div className="api-binom-param">
            <label>chat_id (session_id)</label>
            <input
              type="text"
              placeholder="abc-123-session"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              className="api-binom-input"
            />
          </div>
        </div>
        <RequestBlock
          method="GET"
          path={`/api/v1/chats/${chatId.trim() || "{chatId}"}`}
          description={t("Get full chat details including messages.")}
          apiKey={apiKey}
          onExecute={executeGetChatDetail}
          loading={loading === "chat-detail"}
          requestUrl={showFor("chat-detail").requestUrl}
          response={showFor("chat-detail").response}
          copied={copied}
          onCopy={copy}
        />

        <h3 className="api-binom-h3">{t("Send message")}</h3>
        <div className="api-binom-params">
          <div className="api-binom-param">
            <label>chat_id</label>
            <input
              type="text"
              placeholder="abc-123-session"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              className="api-binom-input"
            />
          </div>
          <div className="api-binom-param">
            <label>message</label>
            <input
              type="text"
              placeholder="Hello!"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              className="api-binom-input"
            />
          </div>
        </div>
        <RequestBlock
          method="POST"
          path="/api/v1/chats/{chatId}/messages"
          description={t("Send a message to an existing chat (body: { message: \"...\" }).")}
          apiKey={apiKey}
          onExecute={executeSendMessage}
          loading={loading === "send-msg"}
          requestUrl={showFor("send-msg").requestUrl}
          response={showFor("send-msg").response}
          copied={copied}
          onCopy={copy}
        />

        <p className="api-binom-note">{t("Example with cURL:")}</p>
        <div className="api-binom-code-block">
          <button type="button" className="api-binom-copy-btn" onClick={() => copy(`curl -H "X-API-Key: ${apiKey || "YOUR_KEY"}" "${API_URL}/api/v1/agents"`, "curl")}>
            {copied === "curl" ? "✓" : "⎘"}
          </button>
          <pre>{`curl -H "X-API-Key: ${apiKey || "YOUR_KEY"}" "${API_URL}/api/v1/agents"`}</pre>
        </div>
      </section>

      <section id="endpoints" className="api-binom-section">
        <h2>{t("Endpoints")}</h2>
        <table className="api-binom-table">
          <thead>
            <tr>
              <th>{t("Method")}</th>
              <th>{t("Endpoint")}</th>
              <th>{t("Description")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>GET</td>
              <td><code>/api/v1/agents</code></td>
              <td>{t("List of agents")}</td>
            </tr>
            <tr>
              <td>GET</td>
              <td><code>/api/v1/chats</code></td>
              <td>{t("List of chats")}</td>
            </tr>
            <tr>
              <td>GET</td>
              <td><code>/api/v1/chats?agent_id=123</code></td>
              <td>{t("Chats by agent")}</td>
            </tr>
            <tr>
              <td>GET</td>
              <td><code>/api/v1/agents/{"{agent_id}"}/chats?include_messages=true</code></td>
              <td>{t("Agent chats with messages (for parsing)")}</td>
            </tr>
            <tr>
              <td>GET</td>
              <td><code>/api/v1/chats/{"{chatId}"}</code></td>
              <td>{t("Chat details")}</td>
            </tr>
            <tr>
              <td>POST</td>
              <td><code>/api/v1/chats/{"{chatId}"}/messages</code></td>
              <td>{t("Send message")}</td>
            </tr>
          </tbody>
        </table>
        <p className="api-binom-note">{t("Base URL")}: <code>{API_URL}</code></p>
      </section>

      <section id="widget" className="api-binom-section">
        <h2>{t("Widget")}</h2>
        <p>{t("Add the chat widget to your site. Replace YOUR_AGENT_ID with your agent id.")}</p>
        <div className="api-binom-code-block">
          <button type="button" className="api-binom-copy-btn" onClick={() => copy(`<script src="${API_URL}/api/widget-loader/YOUR_AGENT_ID"></script>`, "widget")}>
            {copied === "widget" ? "✓" : "⎘"}
          </button>
          <pre>{`<script src="${API_URL}/api/widget-loader/YOUR_AGENT_ID"></script>`}</pre>
        </div>
      </section>
    </div>
  );
}
